import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Table,
  Badge,
  Stack,
  ActionIcon,
} from "@mantine/core";
import { IconEye, IconFileText, IconEdit } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { rfqApi } from "../../../api/rfq";
import { useVendorAuth } from "../auth/VendorAuthContext";
import dayjs from "dayjs";

const statusColors: Record<string, string> = {
  SENT: "blue",
  QUOTES_RECEIVED: "cyan",
  UNDER_REVIEW: "yellow",
  AWARDED: "green",
  CANCELLED: "red",
  EXPIRED: "orange",
};

export function VendorRFQList() {
  const navigate = useNavigate();
  const { vendor } = useVendorAuth();

  const { data: rfqs, isLoading } = useQuery({
    queryKey: ["vendor-rfqs"],
    queryFn: () => rfqApi.getVendorRFQs(),
    enabled: !!vendor,
  });

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading RFQs...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Title order={2} mb="xl">
        RFQ Invitations
      </Title>

      <Paper shadow="sm" p="md">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>RFQ Number</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Deadline</Table.Th>
              <Table.Th>Quotation Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rfqs?.map((rfq) => {
              const vendorMapping = rfq.vendors?.find(
                (v) => v.vendorId === vendor?.id
              );
              const quotation = rfq.quotations?.find(
                (q) => q.vendorId === vendor?.id
              );

              return (
                <Table.Tr key={rfq.id}>
                  <Table.Td>{rfq.rfqNumber}</Table.Td>
                  <Table.Td>{rfq.title}</Table.Td>
                  <Table.Td>
                    <Badge variant="light">{rfq.category}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={statusColors[rfq.status]} variant="light">
                      {rfq.status.replace("_", " ")}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {dayjs(rfq.deadline).format("DD MMM YYYY")}
                  </Table.Td>
                  <Table.Td>
                    {quotation ? (
                      <Badge
                        color={
                          quotation.status === "ACCEPTED"
                            ? "green"
                            : quotation.status === "SUBMITTED"
                            ? "blue"
                            : quotation.status === "DRAFT"
                            ? "yellow"
                            : "gray"
                        }
                        variant="light"
                      >
                        {quotation.status}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        Not Started
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {/* Only show View RFQ button if quotation is not submitted */}
                      {!quotation || quotation.status === "DRAFT" ? (
                        <ActionIcon
                          variant="subtle"
                          onClick={() => navigate(`/vendor/rfq/${rfq.id}`)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      ) : null}
                      {quotation?.status === "DRAFT" ? (
                        <Button
                          size="xs"
                          variant="light"
                          color="yellow"
                          leftSection={<IconEdit size={14} />}
                          onClick={() =>
                            navigate(`/vendor/rfq/${rfq.id}/quotation`)
                          }
                        >
                          Edit Draft
                        </Button>
                      ) : quotation?.status === "SUBMITTED" || quotation?.status === "ACCEPTED" || quotation?.status === "REJECTED" ? (
                        <Button
                          size="xs"
                          variant="light"
                          color="blue"
                          leftSection={<IconEye size={14} />}
                          onClick={() =>
                            navigate(`/vendor/rfq/${rfq.id}/quotation`)
                          }
                        >
                          View Quotation
                        </Button>
                      ) : !quotation ? (
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconFileText size={14} />}
                          onClick={() =>
                            navigate(`/vendor/rfq/${rfq.id}/quotation`)
                          }
                        >
                          Submit Quote
                        </Button>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        {(!rfqs || rfqs.length === 0) && (
          <Stack align="center" py="xl">
            <Text c="dimmed">No RFQ invitations found</Text>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}

